import { useState, useEffect } from "react";
import { applyMarket, applyMarketOne, isBR, MARKET } from "@/lib/market";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useTranslatedContent } from "@/hooks/useTranslatedContent";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Printer,
  Settings,
  Headphones,
  Shield,
  Award,
  CheckCircle2,
  Users,
  TrendingUp,
  Quote,
  Package,
  MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Layout } from "@/components/layout/Layout";

import { SEO } from "@/components/SEO";
import { LatestBlogPosts } from "@/components/homepage/LatestBlogPosts";
import { PromotionsSection } from "@/components/homepage/PromotionsSection";
import { PriceMatchSection } from "@/components/homepage/PriceMatchSection";
import { cn } from "@/lib/utils";
import { PUBLIC_EQUIPMENT_WITH_CATEGORY_SELECT, PUBLIC_CONSUMABLE_WITH_CATEGORY_SELECT } from "@/lib/publicProductSelects";
import whyHexatechImage from "@/assets/why-hexatech-team.jpg";

// Default fallback slides if no banners are configured
const defaultHeroSlides = [
  {
    title: "Impressora DTF Portugal — Distribuidor Oficial",
    subtitle: "Equipamentos DTF profissionais com cabeças Epson i3200, tintas, filme e pó adesivo. Instalação, formação e assistência técnica especializada incluídas.",
    cta: "Ver Impressoras DTF",
    ctaSecondary: "Criar Conta Empresarial",
    link: "/equipamentos",
    linkSecondary: "/auth?mode=register",
    image: "/images/banners/hero-dtf-printing.jpg",
  },
  {
    title: "Tecnologia DTF de Alta Performance em Portugal",
    subtitle: "Impressão DTF têxtil profissional com qualidade superior, cores vibrantes e custos reduzidos. Personalize qualquer tecido sem pré-tratamento, com suporte técnico Epson i3200.",
    cta: "Ver Impressoras DTF",
    ctaSecondary: "Pedir Demonstração",
    link: "/equipamentos/dtf-digital-printer",
    linkSecondary: "/contacto",
    image: "/images/banners/hero-dtf-textile.jpg",
  },
  {
    title: "Impressão UV em Qualquer Superfície",
    subtitle: "Imprima diretamente em madeira, acrílico, vidro, metal e muito mais com impressoras UV profissionais distribuídas pela HEXATECH.",
    cta: "Explorar UV",
    ctaSecondary: "Solicitar Orçamento",
    link: "/equipamentos/uv-machine",
    linkSecondary: "/contacto",
    image: "/images/banners/hero-uv-printing.jpg",
  },
];

const benefits = [
  {
    icon: Award,
    title: "Distribuidor Exclusivo HEXATECH",
    description: "Representante oficial da marca líder em Portugal",
  },
  {
    icon: Settings,
    title: "Suporte Técnico Nacional",
    description: "Assistência especializada em todo o território",
  },
  {
    icon: Headphones,
    title: "Formação Incluída",
    description: "Capacitação completa para a sua equipa",
  },
  {
    icon: Shield,
    title: "Garantia Alargada",
    description: "Proteção total nos equipamentos",
  },
];

const stats = [
  { value: "+ de 200", label: "Clientes Atendidos", icon: Users, description: "Parcerias sólidas em diversos setores industriais." },
  { value: "+ de 300", label: "Equipamentos Instalados", icon: Printer, description: "Sistemas instalados com tecnologia de ponta." },
  { value: "98%", label: "Taxa de Satisfação", icon: CheckCircle2, description: "Excelência comprovada em suporte e operação." },
  { value: "15+", label: "Anos de Experiência", icon: Award, description: "Assistência técnica de qualidade em Portugal." },
];

const steps = [
  {
    number: "01",
    title: "Registe a sua Empresa",
    description: "Crie uma conta empresarial com os dados da sua empresa",
  },
  {
    number: "02",
    title: "Explore o Catálogo",
    description: "Aceda a preços exclusivos B2B e especificações técnicas",
  },
  {
    number: "03",
    title: "Faça a sua Encomenda",
    description: "Compre online ou solicite um orçamento personalizado",
  },
  {
    number: "04",
    title: "Receba e Instale",
    description: "Entrega, instalação e formação incluídas",
  },
];

const testimonials = [
  {
    company: "Gráfica Moderna",
    name: "João Silva",
    role: "Diretor de Produção",
    text: "A HEXATECH transformou a nossa capacidade de produção. Equipamentos de topo e suporte impecável. Recomendo vivamente a qualquer empresa do setor.",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop",
  },
  {
    company: "Print Solutions",
    name: "Ana Costa",
    role: "CEO",
    text: "Parceiro de confiança há 5 anos. Sempre com as melhores soluções para as nossas necessidades. O suporte técnico é extraordinário.",
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop",
  },
  {
    company: "Têxtil Norte",
    name: "Miguel Santos",
    role: "Gestor de Operações",
    text: "A impressora DTF que adquirimos revolucionou o nosso processo de produção têxtil. ROI em menos de 6 meses!",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop",
  },
];

const solutions = [
  {
    title: "Impressão Têxtil",
    description: "DTF, sublimação e serigrafia digital para personalização profissional",
    icon: "👕",
    link: "/equipamentos/dtf-digital-printer",
  },
  {
    title: "Impressão UV",
    description: "Rígidos, brindes promocionais e objetos 3D",
    icon: "🎁",
    link: "/equipamentos/uv-machine",
  },
  {
    title: "Grande Formato",
    description: "Banners, lonas, sinalética e comunicação visual",
    icon: "🖼️",
    link: "/equipamentos/eco-solvent-printer",
  },
  {
    title: "Laminação Profissional",
    description: "Acabamentos de qualidade para máxima durabilidade",
    icon: "✨",
    link: "/equipamentos/laminator",
  },
];

export default function Index() {
  const { t, i18n } = useTranslation();
  const { translateProducts, isTranslating, isPortuguese } = useTranslatedContent();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [featuredTab, setFeaturedTab] = useState<"equipment" | "consumables">("equipment");
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  // Only swap in DB-driven banners after the first paint, so the LCP element
  // stays the locally preloaded hero image instead of a late remote URL.
  const [bannersReady, setBannersReady] = useState(false);

  const [translatedEquipment, setTranslatedEquipment] = useState<any[]>([]);
  const [translatedConsumables, setTranslatedConsumables] = useState<any[]>([]);

  // Fetch dynamic banners from database
  const { data: heroBanners } = useQuery({
    queryKey: ["home-hero-banners"],
    queryFn: async () => {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from("banners")
        .select("*")
        .eq("location", "home-hero")
        .eq("is_active", true)
        .in("market", isBR ? ["br", "all"] : ["pt", "all"])
        .or(`starts_at.is.null,starts_at.lte.${now}`)
        .or(`ends_at.is.null,ends_at.gte.${now}`)
        .order("display_order");
      if (error) throw error;
      return data;
    },
  });

  // Map banners to hero slides format or use defaults with translations
  const defaultHeroSlidesTranslated = isBR ? [
    {
      title: "HEXATECH Brasil — Consumíveis DTF",
      subtitle: "Filme PET, tintas e pó adesivo de alta qualidade, com preços em R$ para empresas com CNPJ.",
      cta: "Ver consumíveis",
      ctaSecondary: "Cadastrar empresa",
      link: "/consumiveis",
      linkSecondary: "/auth?mode=register",
      image: "/images/banners/hero-dtf-textile.jpg",
    },
  ] : [
    {
      title: t("hero.title"),
      subtitle: t("hero.subtitle"),
      cta: t("hero.cta"),
      ctaSecondary: t("hero.ctaSecondary"),
      link: "/equipamentos",
      linkSecondary: "/auth?mode=register",
      image: "/images/banners/hero-dtf-printing.jpg",
    },
    {
      title: t("solutions.textile.title"),
      subtitle: t("solutions.textile.description"),
      cta: t("common.viewEquipment"),
      ctaSecondary: t("common.requestDemo"),
      link: "/equipamentos/dtf-digital-printer",
      linkSecondary: "/contacto",
      image: "/images/banners/hero-dtf-textile.jpg",
    },
    {
      title: t("solutions.uv.title"),
      subtitle: t("solutions.uv.description"),
      cta: t("common.viewMore"),
      ctaSecondary: t("common.requestQuote"),
      link: "/equipamentos/uv-machine",
      linkSecondary: "/contacto",
      image: "/images/banners/hero-uv-printing.jpg",
    },
  ];

  const heroSlides = bannersReady && heroBanners && heroBanners.length > 0
    ? heroBanners.map((banner: any) => {

        const lang = (i18n.language || "pt").split("-")[0];
        const localized = (k: "title" | "subtitle") =>
          (lang !== "pt" && banner[`${k}_${lang}`]) || banner[k] || "";
        return {
          title: localized("title"),
          subtitle: localized("subtitle"),
          cta: banner.link_text || t("common.viewMore"),
          ctaSecondary: t("hero.contact"),
          link: banner.link_url || "/equipamentos",
          linkSecondary: "/contacto",
          image: banner.image_url,
        };
      })
    : defaultHeroSlidesTranslated;

  const { data: featuredEquipment } = useQuery({
    queryKey: ["featured-equipment", MARKET],
    queryFn: async () => {

      const { data, error } = await supabase
        .from("equipment")
        .select(PUBLIC_EQUIPMENT_WITH_CATEGORY_SELECT)
        .eq("is_active", true)
        .eq("is_visible", true)
        .eq("is_featured", true)
        .limit(10);
      if (error) throw error;
      return data as any[];
    },
  });

  const { data: featuredConsumables } = useQuery({
    queryKey: ["featured-consumables", MARKET],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("consumables")
        .select(PUBLIC_CONSUMABLE_WITH_CATEGORY_SELECT)
        .eq("is_active", true)
        .eq("is_visible", true)
        .eq("is_featured", true)
        .limit(10);
      if (error) throw error;
      return applyMarket(data as any[]);
    },
  });

  // Translate products when language changes
  useEffect(() => {
    if (featuredEquipment && !isPortuguese) {
      translateProducts(featuredEquipment).then(setTranslatedEquipment);
    } else if (featuredEquipment) {
      setTranslatedEquipment(featuredEquipment);
    }
  }, [featuredEquipment, isPortuguese, translateProducts]);

  useEffect(() => {
    if (featuredConsumables && !isPortuguese) {
      translateProducts(featuredConsumables).then(setTranslatedConsumables);
    } else if (featuredConsumables) {
      setTranslatedConsumables(featuredConsumables);
    }
  }, [featuredConsumables, isPortuguese, translateProducts]);

  // Remove critical inline content once React renders
  useEffect(() => {
    const criticalBenefits = document.getElementById('critical-benefits');
    if (criticalBenefits) criticalBenefits.remove();

    // Keep the inline hero (already the LCP paint) until the React hero image
    // is decoded, so the LCP element is never swapped for an unpainted one.
    const criticalHero = document.getElementById('critical-hero');
    if (!criticalHero) return;
    const remove = () => criticalHero.remove();
    const img = document.querySelector<HTMLImageElement>('[data-react-hero="true"] img');
    if (!img || img.complete) {
      remove();
      return;
    }
    img.addEventListener('load', remove, { once: true });
    img.addEventListener('error', remove, { once: true });
    const t = window.setTimeout(remove, 3000);
    return () => window.clearTimeout(t);
  }, []);

  // Flip to DB banners only after the browser has painted the first frame.
  useEffect(() => {
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => setBannersReady(true)),
    );
    return () => cancelAnimationFrame(raf);
  }, []);


  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, heroSlides.length]);

  const nextSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  // Use translated data
  const displayEquipment = translatedEquipment.length > 0 ? translatedEquipment : featuredEquipment;
  const displayConsumables = translatedConsumables.length > 0 ? translatedConsumables : featuredConsumables;

  return (
    <Layout>
      
      <SEO 
        title={isBR ? "Consumíveis DTF no Brasil" : "Impressora DTF Portugal"}
        description={isBR ? "HEXATECH Brasil — filme PET, tintas e pó adesivo DTF para empresas com CNPJ. Preços em R$, envio a partir de Blumenau/SC." : "Impressora DTF Portugal — distribuidor oficial HEXATECH. Equipamentos DTF com cabeças Epson i3200, tintas, filme e pó adesivo. Assistência técnica especializada."}
        image="/images/banners/hero-dtf-printing.jpg"
        images={[
          "/images/banners/hero-dtf-printing.jpg",
          "/images/banners/hero-dtf-textile.jpg",
          "/images/banners/hero-uv-printing.jpg",
        ]}
        pageType="WebPage"

        url="/"
        faqs={[
          { question: "O que é impressão DTF?", answer: "DTF (Direct to Film) é uma tecnologia de impressão digital têxtil que permite transferir designs de alta qualidade para qualquer tipo de tecido, incluindo algodão, poliéster e tecidos mistos, sem pré-tratamento." },
          { question: "Quanto custa uma impressora DTF?", answer: "Os preços variam conforme o modelo. A HEXATECH oferece impressoras DTF desde modelos A3 até industriais de 60cm. Contacte-nos para orçamento B2B personalizado com financiamento disponível." },
          { question: "Que consumíveis são necessários para DTF?", answer: "São necessários tintas DTF (CMYK + branco), filme de transferência PET, pó adesivo hot melt e papel de proteção. Stock permanente com entrega em 24-48h." },
          { question: "A HEXATECH faz assistência técnica DTF?", answer: "Sim, oferecemos assistência técnica especializada para impressoras DTF em todo o Portugal, incluindo reparação de cabeças Epson i3200 e manutenção preventiva." },
          { question: "Onde comprar impressora DTF em Portugal?", answer: "A HEXATECH é distribuidor oficial em Barcelos com showroom e demonstrações ao vivo. Compre online em hexatech.pt com entrega nacional." },
        ]}
      />
      {/* Hero Carousel - Fixed height to prevent CLS */}

      <section 
        className="relative h-[clamp(470px,80vh,580px)] md:h-[clamp(420px,36.5vw,700px)] overflow-hidden bg-foreground" 
        style={{ contain: 'layout paint' }}
        data-react-hero="true"
      >
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={cn(
              "absolute inset-0 transition-opacity duration-1000 will-change-opacity",
              index === currentSlide ? "opacity-100 z-10" : "opacity-0 z-0"
            )}
            style={{ contain: 'strict' }}
          >
            {/* Background Image with Overlay - Using img for LCP optimization */}
            {/* translate-y-4 adds a small top safe-area so text like “MATERIAIS” isn’t flush/cut at the hero edge */}
            <div className="absolute inset-0 overflow-hidden bg-background">
              {(index === 0 || index === currentSlide) && (
                <img
                  src={slide.image}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover object-center"
                  width={1920}
                  height={700}
                  {...{ fetchpriority: index === 0 ? "high" : "low" }}
                  loading={index === 0 ? "eager" : "lazy"}
                  decoding={index === 0 ? "sync" : "async"}
                />

              )}

              {/* Soft left gradient for text legibility */}
              <div className="absolute inset-0 bg-gradient-to-r from-foreground/75 via-foreground/35 to-transparent" />
            </div>

            <div className="container relative z-10 h-full flex items-center pb-16 md:pb-0">
              <div className="w-full max-w-xl text-background">
                <h2
                  className={cn(
                    "text-2xl sm:text-3xl md:text-4xl font-bold mb-3 leading-tight tracking-tight",
                    index === currentSlide && "animate-fade-in"
                  )}
                >
                  {slide.title}
                </h2>
                <p
                  className={cn(
                    "text-sm md:text-base text-background/85 mb-5 leading-relaxed line-clamp-2",
                    index === currentSlide && "animate-fade-in"
                  )}
                  style={{ animationDelay: "0.1s" }}
                >
                  {slide.subtitle}
                </p>
                <div
                  className={cn(index === currentSlide && "animate-fade-in")}
                  style={{ animationDelay: "0.2s" }}
                >
                  <Button asChild className="bg-primary hover:bg-primary/90">
                    <Link to={slide.link}>
                      {slide.cta}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>

          </div>
        ))}

        {/* Carousel Controls */}
        <div className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            className="rounded-full bg-foreground/10 border-foreground/20 text-foreground hover:bg-foreground/20 backdrop-blur-sm"
            onClick={prevSlide}
            aria-label="Slide anterior"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </Button>
          <div className="flex gap-2" role="tablist" aria-label="Slides do carrossel">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setIsAutoPlaying(false);
                  setCurrentSlide(index);
                }}
                className="relative flex items-center justify-center min-w-6 min-h-6 p-2"
                aria-label={`Ir para slide ${index + 1}`}
                aria-selected={index === currentSlide}
                role="tab"
              >
                <span
                  className={cn(
                    "h-2 rounded-full transition-all duration-300",
                    index === currentSlide
                      ? "bg-primary w-8"
                      : "bg-foreground/30 w-2 hover:bg-foreground/50"
                  )}
                />
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="icon"
            className="rounded-full bg-foreground/10 border-foreground/20 text-foreground hover:bg-foreground/20 backdrop-blur-sm"
            onClick={nextSlide}
            aria-label="Próximo slide"
          >
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </Button>
        </div>
      </section>

      {/* Visible page H1 — main topical context for users and crawlers */}
      <section className="bg-background py-5 md:py-6">
        <div className="container flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-8">
          <h1 className="text-lg md:text-2xl font-bold text-foreground leading-tight">
            {isBR ? "Consumíveis DTF no Brasil — HEXATECH Brasil" : "Impressora DTF Portugal — Distribuidor Oficial HEXATECH"}
          </h1>
          <p className="max-w-xl text-sm text-muted-foreground">
            {isBR
              ? "Filme PET, tintas e pó adesivo DTF de alta qualidade. Vendas exclusivas para empresas com CNPJ, a partir de Blumenau/SC."
              : "Equipamentos DTF com cabeças Epson i3200, tintas, filme PET e pó adesivo. Assistência técnica, reparação de cabeças e stock permanente em Portugal."}
          </p>
        </div>
      </section>



      {/* Stats Bar — compact strip */}
      {!isBR && <section className="py-1.5 md:py-2 bg-navy relative overflow-hidden">
        {/* Industrial gold accent line */}
        <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gold to-transparent opacity-60" />

        <div className="container relative z-10">
          <div className="grid grid-cols-4 divide-x divide-navy-foreground/10">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="flex flex-col items-center justify-center px-1 py-1 text-center"
              >
                <h3 className="text-sm md:text-lg font-bold tracking-tight text-navy-foreground leading-tight">
                  {stat.value}
                </h3>
                <p className="text-[8px] md:text-[10px] font-bold uppercase tracking-[0.08em] md:tracking-[0.15em] text-gold leading-tight">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>}

      {/* Featured products — single tabbed showcase */}
      {((displayEquipment?.length ?? 0) > 0 || (displayConsumables?.length ?? 0) > 0) && (() => {
        const tab = featuredTab === "equipment" && (displayEquipment?.length ?? 0) > 0
          ? "equipment"
          : (displayConsumables?.length ?? 0) > 0 ? "consumables" : "equipment";
        const items = (tab === "equipment" ? displayEquipment : displayConsumables) || [];
        return (
          <section className="py-2 md:py-10 bg-muted border-y border-border/50">
            <div className="container px-2 md:px-8">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-1.5 md:gap-4 mb-2 md:mb-6 px-1 md:px-0">
                <div>
                  <h2 className="text-lg md:text-3xl font-bold text-foreground mb-1.5 md:mb-3">Produtos em destaque</h2>
                  <div className="inline-flex rounded-lg border border-border bg-background p-1" role="tablist">
                    {[
                      { key: "equipment" as const, label: "Equipamentos", count: displayEquipment?.length ?? 0 },
                      { key: "consumables" as const, label: "Consumíveis & Tintas", count: displayConsumables?.length ?? 0 },
                    ].filter((o) => o.count > 0).map((o) => (
                      <button
                        key={o.key}
                        role="tab"
                        aria-selected={tab === o.key}
                        onClick={() => setFeaturedTab(o.key)}
                        className={cn(
                          "px-3 py-1 text-xs md:text-sm font-medium rounded-md transition-colors",
                          tab === o.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
                <Button variant="link" asChild className="text-primary h-auto p-0 w-fit text-sm md:h-10 md:px-4 md:border md:border-primary">
                  <Link to={tab === "equipment" ? "/equipamentos" : "/consumiveis"}>
                    Ver catálogo completo
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
              <div className="-mx-2 md:mx-0 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-4">
                {items.slice(0, 6).map((item: any) => {
                  const href = tab === "equipment" ? `/equipamento/${item.slug}` : `/consumivel/${item.slug}`;
                  const category = tab === "equipment" ? item.equipment_categories?.name : item.consumables_categories?.name;
                  const Icon = tab === "equipment" ? Printer : Package;
                  return (
                    <Link key={item.id} to={href}>
                      <Card className="group overflow-hidden h-full border-border/50 hover:border-primary/50 hover:shadow-corporate-lg transition-all duration-300">
                        <div className="aspect-square md:aspect-[4/3] relative overflow-hidden bg-muted">
                          {item.image_url ? (
                            <img src={item.image_url} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" width={300} height={300} />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center"><Icon className="h-12 w-12 text-muted-foreground" /></div>
                          )}
                          {category && <Badge className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px]">{category}</Badge>}
                        </div>
                        <CardContent className="p-2 md:p-3">
                          <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-1 text-sm">{item.name}</h3>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })()}

      {/* Promotions Section (PT only — BR has its own catalogue) */}
      {!isBR && <PromotionsSection />}

      {isBR && (displayConsumables?.length ?? 0) === 0 && (
        <section className="py-10 bg-muted border-y border-border/50">
          <div className="container text-center max-w-2xl">
            <h2 className="text-2xl font-bold text-foreground mb-2">Catálogo Brasil em preparação</h2>
            <p className="text-muted-foreground mb-5">Estamos finalizando os produtos com preços em R$. Fale com a gente pelo WhatsApp ou cadastre sua empresa para ser avisado.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button asChild><a href="https://wa.me/554792220050" target="_blank" rel="noopener noreferrer">Falar no WhatsApp</a></Button>
              <Button variant="outline" asChild><Link to="/auth?mode=signup">Cadastrar empresa</Link></Button>
            </div>
          </div>
        </section>
      )}

      {/* Price Match Guarantee — near products, where prices are evaluated */}
      {!isBR && <PriceMatchSection />}

      {!isBR && <>
      {/* Why Us - with data attribute for critical skeleton replacement */}
      <section className="py-14 md:py-20 bg-background border-y border-border" data-react-benefits="true">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 mb-4">
                <span className="h-px w-8 bg-primary" />
                <p className="text-primary font-semibold uppercase tracking-[0.18em] text-xs">{t("whyUs.sectionLabel")}</p>
              </div>
              <h2 className="text-3xl md:text-[2.6rem] leading-[1.1] font-bold text-foreground mb-5 tracking-tight">
                {t("whyUs.title")}
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed max-w-xl">
                {t("whyUs.subtitle")}
              </p>
              <div className="grid sm:grid-cols-2 gap-px bg-border border border-border rounded-xl overflow-hidden">
                {benefits.map((benefit, index) => (
                  <div
                    key={index}
                    className="group flex items-start gap-3 p-5 bg-card hover:bg-muted/60 transition-colors"
                  >
                    <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <benefit.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground text-sm mb-1">{benefit.title}</h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Button asChild size="lg" className="bg-primary hover:bg-primary/90">
                  <Link to="/sobre">
                    {t("index.knowHexatech")}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/contacto">{t("nav.contact", "Falar com um especialista")}</Link>
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden border border-border shadow-corporate-lg">
                <img
                  src={whyHexatechImage}
                  alt="Técnicos Hexatech em assistência a equipamento de impressão digital"
                  loading="lazy"
                  decoding="async"
                  width={1024}
                  height={1024}
                  className="w-full h-full object-cover aspect-[4/5] sm:aspect-[4/3] lg:aspect-square"
                />
              </div>
              <div className="hidden lg:block absolute -top-4 -right-4 h-24 w-24 border-t-2 border-r-2 border-primary/40 rounded-tr-2xl pointer-events-none" />
            </div>
          </div>
        </div>
      </section>


      {/* How It Works */}
      <section className="relative py-14 md:py-20 bg-navy text-navy-foreground overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(hsl(0 0% 100%) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100%) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        <div className="container relative">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="h-px w-8 bg-gold" />
              <p className="text-gold font-semibold uppercase tracking-[0.18em] text-xs">{t("howItWorks.sectionLabel")}</p>
              <span className="h-px w-8 bg-gold" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight">
              {t("howItWorks.title")}
            </h2>
            <p className="text-base md:text-lg text-navy-foreground/70 max-w-2xl mx-auto">
              {t("howItWorks.subtitle")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { number: "01", key: "register" },
              { number: "02", key: "explore" },
              { number: "03", key: "order" },
              { number: "04", key: "receive" },
            ].map((step, index, arr) => (
              <div key={index} className="relative">
                <div className="h-full rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-6 hover:border-gold/50 hover:bg-white/[0.07] transition-colors">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl font-bold text-gold leading-none">{step.number}</span>
                    <span className="h-px flex-1 bg-white/15" />
                  </div>
                  <h3 className="font-semibold mb-2">{t(`howItWorks.steps.${step.key}.title`)}</h3>
                  <p className="text-sm text-navy-foreground/70 leading-relaxed">{t(`howItWorks.steps.${step.key}.description`)}</p>
                </div>
                {index < arr.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3.5 z-10">
                    <ArrowRight className="h-5 w-5 text-gold/60" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* B2B conversion CTA — showroom & sample */}
      <section className="py-10 md:py-14 bg-background">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-4">
            <Link to="/contacto" className="group rounded-xl border border-border bg-card p-6 hover:border-primary/50 hover:shadow-corporate-lg transition-all">
              <MapPin className="h-6 w-6 text-primary mb-3" />
              <h3 className="text-lg font-bold text-foreground mb-1">Demonstração no Showroom</h3>
              <p className="text-sm text-muted-foreground mb-4">Veja as máquinas DTF e UV a imprimir ao vivo em Tamel, Barcelos, antes de decidir.</p>
              <span className="inline-flex items-center text-sm font-semibold text-primary">Agendar visita <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
            </Link>
            <Link to="/contacto" className="group rounded-xl border border-primary/30 bg-primary/5 p-6 hover:border-primary hover:shadow-corporate-lg transition-all">
              <Package className="h-6 w-6 text-primary mb-3" />
              <h3 className="text-lg font-bold text-foreground mb-1">Amostra DTF Grátis</h3>
              <p className="text-sm text-muted-foreground mb-4">Teste a qualidade da impressão e a aderência no seu tecido, sem compromisso.</p>
              <span className="inline-flex items-center text-sm font-semibold text-primary">Pedir amostra <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
            </Link>
          </div>
        </div>
      </section>

      {/* Latest Blog Posts - SEO Content */}
      <LatestBlogPosts />

      {/* Testimonials */}
      <section className="py-14 md:py-20 bg-muted border-y border-border">
        <div className="container">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="h-px w-8 bg-primary" />
              <p className="text-primary font-semibold uppercase tracking-[0.18em] text-xs">{t("testimonials.sectionLabel")}</p>
              <span className="h-px w-8 bg-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">
              {t("testimonials.title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {["1", "2", "3"].map((num, index) => (
              <Card key={index} className="border-border bg-card shadow-sm hover:shadow-corporate-lg transition-shadow">
                <CardContent className="p-6">

                  <Quote className="h-8 w-8 text-primary/30 mb-4" />
                  <p className="text-muted-foreground mb-6 italic">&ldquo;{t(`testimonials.clients.${num}.text`)}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">
                      {String(t(`testimonials.clients.${num}.name`)).split(" ").map((w) => w[0]).slice(0, 2).join("")}
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{t(`testimonials.clients.${num}.name`)}</p>
                      <p className="text-sm text-muted-foreground">{t(`testimonials.clients.${num}.role`)}, {t(`testimonials.clients.${num}.company`)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>


      </>}
    </Layout>
  );
}
